import React, { useEffect, useState } from "react";
import { Button, Col, Form, Row } from "react-bootstrap";
import {
    Controller,
    FormProvider,
    SubmitHandler,
    useFieldArray,
    useForm,
} from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import ESInput from "../../controls/ESInput";
import SubHeader from "../subheader/Subheader";
import Header from "../header/Header";
import { showToast, ToastTypeCode } from "../../utils/Constant";
import { axiosPost } from "../../utils/AxiosHelper";
import { getSurveyFormByIdForPoll, insertPoll, surveyPoll, updatePoll } from "../../utils/ApiUrl";
import { decrypt } from "../../utils/common";
import "./AddPoll.scss";
import Loader from "../loader/Loader";
import "./SurveyformManager.scss";

interface Option {
    value: string;
}

interface OptionFetch {
    Value: string;
}

interface QuestionForm {
    QuestionId?: number;
    QuestionTitle: string;
    Instruction?: string;
    LanguageId?: number;
    IsActive: number;
    IsRequired: number;
    ResponseTypeId?: number | null;
    OrganizationId?: number;
    CreateUserId?: number;
    CreateDate?: string;
    UpdateUserId?: number | null;
    UpdateDate?: string | null;
    InternalKey?: string;
    ConcurrencyKey?: string;
    options: Option[];
}

interface AddPollProps {
    pollId: number;
}

const AddPoll: React.FC<AddPollProps> = ({ pollId }) => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [formId, setFormId] = useState<number>();
    const { id } = useParams();
    const [allowMultiSelect, setAllowMultiSelect] = useState(false);

    const handleToggle = () => {
        setAllowMultiSelect((prev) => !prev);
    };

    const fetchData = async () => {
        const surveyFormIdUrl = await decrypt(id);
        setFormId(surveyFormIdUrl);
        fetchPollById(surveyFormIdUrl);
    };

    useEffect(() => {
        if (id) {
            fetchData();
        }
    }, [id]);

    const fetchPollById = async (surveyFormId: number) => {
        setLoading(true);
        try {
            const response: any = await axiosPost(getSurveyFormByIdForPoll, {
                SurveyFormId: surveyFormId,
            });
            if (response?.data?.Status === 200) {
                const data = response?.data?.ExtraData;
                const prefilledValues: QuestionForm = {
                    QuestionId: data?.QuestionId,
                    QuestionTitle: data?.QuestionTitle,
                    Instruction: data?.QuestionInstruction,
                    IsActive: 1,
                    IsRequired: 0,
                    OrganizationId: data?.OrganizationId,
                    CreateUserId: data?.CreateUserId,
                    CreateDate: data?.CreateDate,
                    UpdateUserId: null, 
                    UpdateDate: null,
                    InternalKey: data?.InternalKey,
                    ConcurrencyKey: data?.ConcurrencyKey,
                    options: data.Options?.map((opt: OptionFetch) => ({ value: opt?.Value })) || [],
                }
                if(data?.ResponseTypeId === 1){
                    setAllowMultiSelect(true);
                }
                methods.reset(prefilledValues);
            } else {
                showToast(`${response?.data?.ErrorMessage}`, ToastTypeCode?.ERROR);
            }
        } catch (error) {
            showToast("Error fetching form data", ToastTypeCode?.ERROR);
        } finally {
            setLoading(false);
        }
    }

    const methods = useForm<QuestionForm>({
        defaultValues: {
            options: [{ value: "" }],
        },
    });

    const { fields, append, remove } = useFieldArray({
        control: methods.control,
        name: "options",
    });

    const onSubmit: SubmitHandler<QuestionForm> = async (data) => {
        if (data?.options?.length < 2) {
            showToast("At least two options are required for the poll. ", ToastTypeCode?.ERROR);
            return;
        }

        setLoading(true);
        try {
            const response: any = await axiosPost(
                formId ? updatePoll : insertPoll,
                {
                    SurveyFormId: formId ? formId : 0,
                    QuestionTitle: data?.QuestionTitle,
                    Instruction: data?.Instruction,
                    Options: data?.options,
                    ResponseTypeId: allowMultiSelect ? 1 : 2,
                }
            );

            if (response?.data?.Status === 200) {
                showToast(`Poll ${formId ? "updated" : "added"
                    } successfully`, ToastTypeCode?.SUCCESS);
                navigate(surveyPoll);
            } else {
                showToast(`${response?.data?.ErrorMessage}`, ToastTypeCode?.ERROR);
            }
        } catch (error) {
            console.error("Error submitting poll data", error);
            showToast("Error saving Poll", ToastTypeCode?.ERROR);
        } finally {
            setLoading(false);
        }
    };

    return (
      <div className="form-card">
        <Header />
        <SubHeader />
        <div className="poll-wrapper">
          {loading ? (
            <Loader />
          ) : (
            <div className="container-md">
              <div className="card">
                <div className="card-body">
                  <FormProvider {...methods}>
                    <Form onSubmit={methods.handleSubmit(onSubmit)} data-tour="add-poll-information">
                      <Row>
                        <Form.Group
                          as={Col}
                          lg="6"
                          className="mb-3"
                          controlId="QuestionTitle"
                        >
                          <Form.Label>
                            Question Title{" "}
                            <span className="text-danger">*</span>
                          </Form.Label>

                          <ESInput
                            className="form-control"
                            name="QuestionTitle"
                            placeholder="Question Title"
                            type="text"
                            required
                            minLength={2}
                            maxLength={450}
                          />
                        </Form.Group>
                                                
                      </Row>
                      <Row>
                        <Col lg={6}>
                        <Form.Group
                          className="mb-3"
                          controlId="Instruction"
                        >
                          <Form.Label>Instruction (Optional)</Form.Label>
                          <ESInput
                            className="form-control textarea"
                            name="Instruction"
                            placeholder="Instruction"
                            type="textarea"
                          />
                        </Form.Group>
                        </Col>
                      </Row>
                      <Row>
                        <Col>
                        <div className="toggle-container">
                                <label htmlFor="toggle">
                                    Allow Multi Select
                                </label>
                                <Form.Check
                                    type="switch"
                                    id="toggle"
                                    label=""
                                    checked={allowMultiSelect}
                                    onChange={handleToggle}
                                />
                            </div>
                            </Col>
                      </Row>
                      
                      
                      <Row>
                        <Col lg={6}>
                          <Button
                            type="button"
                            className="btn-primary-outline add-option mb-3"
                            onClick={() => append({ value: "" })}
                            data-tour="add-poll-options"
                          >
                            Add Options
                          </Button>
                        </Col>
                      </Row>
                      <Row>
                      <Col lg="6">
                          {/* <Form.Label>Options</Form.Label> */}
                          <div className="option-list">
                            {fields?.map((field, index) => (
                              <div key={field.id} className="option-item">
                                <Form.Group>
                                  <div className="d-flex mb-2 align-items-center">
                                    <div className="position-relative w-100 d-flex align-items-start justify-content-between gap-2">
                                      <Controller
                                        name={`options.${index}.value`}
                                        control={methods.control}
                                        rules={{
                                          required: {
                                            value: true,
                                            message: "Option value is required",
                                          },
                                          validate: (value) => {
                                            if (value.trim() === "") {
                                              return "Option cannot be empty";
                                            }
                                            return true;
                                          },
                                        }}
                                        render={({
                                          field,
                                          fieldState: { error },
                                        }) => (
                                          <div className="w-100">
                                            <Form.Control
                                              className="form-control pr-4"
                                              placeholder={`Option ${
                                                index + 1
                                              }`}
                                              type="text"
                                              {...field}
                                              isInvalid={!!error}
                                            />
                                            {error && (
                                              <Form.Text className="text-danger">
                                                {error.message}
                                              </Form.Text>
                                            )}
                                          </div>
                                        )}
                                      />
                                      <button
                                        type="button"
                                        onClick={() => remove(index)}
                                        className="input-close"
                                        style={{
                                          position: "absolute",
                                          right: "10px",
                                          background: "transparent",
                                          border: "none",
                                          cursor: "pointer",
                                          top: "6px",
                                        }}
                                      >
                                        &times;
                                      </button>
                                    </div>
                                  </div>
                                </Form.Group>
                              </div>
                            ))}
                          </div>
                        </Col>                        
                      </Row>
                      <Row>
                        <Col>
                        <Form.Group as={Col} md="12" className="mb-3">
                          <Button
                            type="submit"
                            className="primary-btn save-btn"
                            data-tour="add-poll-info"
                          >
                            {id ? "Update" : "Create"}
                          </Button>
                          <Button
                            type="button"
                            className="btn-primary back-btn"
                            onClick={() => navigate("/poll")}
                          >
                            Cancel
                          </Button>
                        </Form.Group>
                        </Col>
                      </Row>
                      <Row>
                        <Col></Col>
                      </Row>
                    </Form>
                  </FormProvider>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    );
};

export default AddPoll;
